<template>
  <div class="page flex-col">
    <PageHeader />

    <!-- 顶部横幅 -->
    <ChannelTopBar />

    <!-- 主体内容：1400px，左 1040px + 右 320px，间隔 40px -->
    <div class="channel-main flex-row">
      <!-- 左侧内容 -->
      <div class="channel-left flex-col">
        <!-- 轮播图 -->
        <div v-if="carouselItems.length > 0">
          <ChannelSwiper :items="carouselItems" />
        </div>

        <!-- 新闻列表（卡片 / 列表切换） -->
        <ChannelNews  v-if="carouselLoaded" :category-id="categoryId" :exclude-news-id-list="excludeNewsIdList" />
      </div>

      <!-- 右侧内容 -->
      <div class="channel-right flex-col">
        <!-- 杂志 -->
        <IssuePanel />

        <!-- 热点榜 -->
        <HotList />

        <!-- 广告位（动态） -->
        <div class="right-images flex-col">
          <a
            v-for="(ad, idx) in adList"
            :key="'ad-' + idx"
            :href="ad.turnUrl"
            target="_blank"
            class="right-ad-item"
          >
            <img
              class="right-ad-image"
              referrerpolicy="no-referrer"
              :src="ad.imageUrl"
              :alt="ad.textTip || '广告'"
              @load="$event => this.handleImageLoad(ad)"
              @error="$event => this.handleImageError(ad)"
              :class="{ 'img-loading': !ad.loaded }"
            />
            <span v-if="!ad.loaded && ad.isLoading" class="img-placeholder">加载中...</span>
            <span v-if="!ad.loaded && !ad.error" class="img-error-btn" @click="this.handleImageLoad(ad)">重新加载</span>
            <span v-if="ad.error" class="img-default">暂无图片</span>
            <span v-if="ad.isShowTag === 1" class="ad-tag">广告</span>
          </a>
        </div>
      </div>
    </div>

    <PageFooter />

    <!-- ================= 回到顶部按钮 ================= -->
    <button 
      v-show="showBackToTop" 
      class="back-to-top" 
      @click="scrollToTop"
      title="回到顶部"
      aria-label="回到顶部"
    >
      <!-- 使用你提供的图片链接替换了原来的 SVG -->
      <img 
        src="https://image.cbrand.com.cn/default/2026/10/2D3A47A40740459C98A490CF835B4529.png" 
        alt="回到顶部" 
        class="back-to-top-icon"
      />
    </button>
    <!-- =============================================== -->

  </div>
</template>

<script>
import PageHeader from '../detail/components/PageHeader.vue'
import PageFooter from '../detail/components/PageFooter.vue'
import IssuePanel from '../detail/components/IssuePanel.vue'
import HotList from '../detail/components/HotList.vue'
import { fetchAdDetails, resolveAdImage, fetchNewsListL2, mapNewsItem } from '../../api'
import ChannelTopBar from './components/ChannelTopBar.vue'
import ChannelSwiper from './components/ChannelSwiper.vue'
import ChannelNews from './components/ChannelNews.vue'

export default {
  name: 'ChannelOne',
  components: {
    PageHeader,
    PageFooter,
    IssuePanel,
    HotList,
    ChannelTopBar,
    ChannelSwiper,
    ChannelNews
  },
  data() {
    return {
      adList: [],
      categoryId: null,
      carouselItems: [],
      excludeNewsIdList: [],
      // 默认无图片占位符
      DEFAULT_IMAGE: require('../../assets/images/default/default-news.png'),
      carouselLoaded: false, // 标记轮播数据是否加载完成
      
      // 控制回到顶部按钮显示
      showBackToTop: false 
    }
  },
  // 监听滚动事件
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  // 组件销毁前移除监听，防止内存泄漏
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  created() {
    this.categoryId = this.$route.query.categoryId
    this.loadAds()
    // 使用 nextTick 确保轮播数据和排除列表都已准备好
    this.$nextTick(() => {
      this.loadCarousel()
    })
  },
  methods: {
    // 处理滚动事件
    handleScroll() {
      // 当页面垂直滚动超过 300px 时显示按钮
      this.showBackToTop = window.scrollY > 300
    },
    // 平滑回到顶部
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    },
    async loadCarousel() {
      if (!this.categoryId) return
      try {
        const { list } = await fetchNewsListL2({
          categoryId: this.categoryId,
          isRecommended: 1,
          pageNum: 1,
          pageSize: 6
        })
        this.carouselItems = list.map(mapNewsItem)
        // 设置需要排除的新闻 ID 列表，让新闻列表接口不再查询这些推荐新闻
        const idList = list.map(item => item.id).filter(Boolean)
        this.excludeNewsIdList = idList
        console.log('[ChannelOne] loadCarousel, excludeNewsIdList:', idList, 'total items:', list.length)
        
        // 确保在 Vue 响应式更新后将排除列表传递给子组件
        this.$nextTick(() => {
          console.log('[ChannelOne] excludeNewsIdList 已更新为:', this.excludeNewsIdList)
        })
      } catch (e) {
        console.error('轮播数据加载失败', e)
        // 即使加载失败，也要清空排除列表，避免影响正常请求
        this.excludeNewsIdList = []
      } finally {
        // 无论成功失败，都放开渲染，防止新闻列表永远不显示
        this.carouselLoaded = true
      }
    },
    async loadAds() {
      const list = await fetchAdDetails('cate_t1_hot_btm')
      this.adList = (list || []).map(item => {
        const imageUrl = resolveAdImage(item.imageUrl)
        // 如果图片地址为空或无效，使用默认占位图
        const finalImageUrl = !imageUrl || imageUrl.trim() === '' ? this.DEFAULT_IMAGE : imageUrl
        
        return {
          imageUrl: finalImageUrl,
          turnUrl: item.turnUrl || '#',
          textTip: item.textTip || '广告',
          isShowTag: item.isShowTag,
          loaded: false,       // 是否已加载完成
          isLoading: true,     // 是否正在加载
          error: false         // 是否加载失败
        }
      })
    },
    handleImageLoad(adItem) {
      adItem.loaded = true
      adItem.isLoading = false
    },
    handleImageError(adItem) {
      adItem.error = true
      adItem.isLoading = false
      // 设置为默认图片
      adItem.imageUrl = this.DEFAULT_IMAGE
    },
  }
}
</script>

<style scoped>
.page {
  width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  background: #fff;
}

/* ===== 图片加载状态样式 ===== */
.right-ad-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 4px;
  opacity: 1;
  transition: opacity 0.3s ease;
}

/* 加载中状态 */
.img-loading {
  opacity: 0.3;
}

/* 图片占位符 */
.img-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  color: #999;
  z-index: 1;
}

/* 重新加载按钮 */
.img-error-btn {
  position: absolute;
  bottom: 12px;
  right: 12px;
  padding: 4px 12px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  z-index: 2;
  transition: all 0.2s;
}

.img-error-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

/* 默认无图片 */
.img-default {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  color: #bbb;
  z-index: 0;
}

/* 主体内容容器 */
.channel-main {
  width: 1400px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 40px;
  gap: 40px;
  align-items: flex-start;
}

/* 左侧内容区 */
.channel-left {
  width: 1040px;
  flex-shrink: 0;
}

/* 右侧内容区 */
.channel-right {
  width: 320px;
  flex-shrink: 0;
}

/* 右侧两张图片 */
.right-images {
  margin-top: 24px;
  gap: 16px;
}

.right-ad-item {
  display: block;
  width: 100%;
  position: relative;
}

.right-ad-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 4px;
  opacity: 1;
  transition: opacity 0.3s ease;
}

.ad-tag {
  position: absolute;
  top: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 0 4px 0 4px;
  line-height: 1.5;
}

/* ================= 回到顶部按钮样式 ================= */
.back-to-top {
  position: fixed;    /* 固定定位，不受页面滚动影响 */
  bottom: 50px;       /* 距离屏幕底部 */
  right: 50px;        /* 距离屏幕右侧 */
  width: 54px;
  height: 54px;
  background-color: transparent; /* 背景透明，因为图片本身带有圆形 */
  border: none;                  /* 移除边框 */
  border-radius: 50%;            /* 圆形，方便点击区域 */
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;                  /* 保证在最上层 */
  padding: 0;
  outline: none;
  transition: all 0.3s ease-in-out;
  /* 添加一点阴影让按钮更立体（如果图片本身不带阴影的话） */
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
}

/* 按钮内部图片样式 */
.back-to-top-icon {
  width: 100%;
  height: 100%;
  object-fit: contain; /* 保持图片比例 */
  display: block;
}

/* 鼠标悬停效果 */
.back-to-top:hover {
  transform: scale(1.1); /* 放大 */
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.25)); /* 阴影加深 */
}

/* 点击时的按压效果 */
.back-to-top:active {
  transform: scale(0.95); /* 缩小 */
}
</style>